import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { paths } from './types';
import { api, request } from './client';

/**
 * Helpers tipados sobre `paths` do OpenAPI (T010).
 *
 * Uso:
 *   type Shell = ApiData<'/api/v1/overview/shell', 'get'>;
 *   const shell = await get('/api/v1/overview/shell');
 *   const criado = await post('/api/v1/users', { body: { … } });
 *
 * `ApiData` já desconta o envelope: o interceptor de `client.ts` entrega o `data`,
 * e o schema de resposta do contrato é `allOf[Envelope, {data: T}]`, então o tipo
 * útil é `…['data']`.
 */

export type ApiPath = keyof paths;
export type Method<P extends ApiPath> = keyof paths[P] & ('get' | 'post' | 'put' | 'delete');

type Op<P extends ApiPath, M extends Method<P>> = paths[P][M];

// openapi-typescript gera as chaves de status como literais numéricos (200, 201…).
//
// `202` entrou em 29/08 com o `runDiscovery`, que passou a **disparar** a varredura
// em vez de esperá-la. Sem ele aqui, `ApiData<'/api/v1/discovery/run', 'post'>`
// resolveria para `never` e a chamada perderia o tipo em silêncio.
type OkStatus = 200 | 201 | 202 | 204;

type OkResponse<O> = O extends { responses: infer R }
  ? Extract<keyof R, OkStatus> extends never
    ? never
    : R[Extract<keyof R, OkStatus>]
  : never;

type JsonBody<T> = T extends { content: { 'application/json': infer J } } ? J : never;

/** Corpo da resposta de sucesso (envelope inteiro). */
export type ApiEnvelope<P extends ApiPath, M extends Method<P>> = JsonBody<OkResponse<Op<P, M>>>;

/**
 * `data` da resposta de sucesso — o que os hooks recebem.
 * O contrato responde `allOf: [Envelope, { data: <T> }]`, então o acesso indexado
 * na interseção já entrega `<T>` (o `data: unknown` do `Envelope` se dissolve).
 */
export type ApiData<P extends ApiPath, M extends Method<P> = Extract<Method<P>, 'get'>> =
  ApiEnvelope<P, M> extends { data?: unknown }
    ? NonNullable<ApiEnvelope<P, M>['data']>
    : ApiEnvelope<P, M>;

/** Corpo aceito na requisição. */
export type ApiBody<P extends ApiPath, M extends Method<P>> =
  Op<P, M> extends {
    requestBody?: { content: { 'application/json': infer B } };
  }
    ? B
    : Op<P, M> extends { requestBody: { content: { 'application/json': infer B2 } } }
      ? B2
      : never;

/** Query string aceita. */
export type ApiQuery<P extends ApiPath, M extends Method<P>> =
  Op<P, M> extends {
    parameters: { query?: infer Q };
  }
    ? Q
    : never;

export interface CallOptions<P extends ApiPath, M extends Method<P>> {
  params?: ApiQuery<P, M>;
  body?: ApiBody<P, M>;
  /** Repassado ao axios (headers, signal, …). */
  config?: Omit<AxiosRequestConfig, 'url' | 'method' | 'params' | 'data'>;
  /**
   * Teto desta chamada, em milissegundos. Sem ele vale o do cliente
   * (`TIMEOUT_PADRAO_MS`, 20 s) — o padrão **não** muda.
   *
   * Existe como campo próprio, e não escondido em `config`, porque é decisão de
   * produto: a ordem demora e a tela promete quanto (`TIMEOUT_ORDEM_LONGA_MS`).
   * Assim `grep timeoutMs` acha todas as rotas longas de uma vez.
   */
  timeoutMs?: number;
  client?: AxiosInstance;
}

function call<P extends ApiPath, M extends Method<P>>(
  method: M,
  url: P | string,
  options: CallOptions<P, M> = {},
): Promise<ApiData<P, M>> {
  const { params, body, config, timeoutMs, client } = options;
  return request<ApiData<P, M>>(
    {
      ...config,
      method: method as string,
      url: url as string,
      params,
      data: body,
      // Depois do spread de propósito: `timeoutMs` explícito ganha do `config`.
      ...(timeoutMs === undefined ? {} : { timeout: timeoutMs }),
    },
    client ?? api,
  );
}

export function get<P extends ApiPath>(
  url: P,
  options?: CallOptions<P, Extract<Method<P>, 'get'>>,
): Promise<ApiData<P, Extract<Method<P>, 'get'>>> {
  return call('get' as Extract<Method<P>, 'get'>, url, options);
}

export function post<P extends ApiPath>(
  url: P,
  options?: CallOptions<P, Extract<Method<P>, 'post'>>,
): Promise<ApiData<P, Extract<Method<P>, 'post'>>> {
  return call('post' as Extract<Method<P>, 'post'>, url, options);
}

export function put<P extends ApiPath>(
  url: P,
  options?: CallOptions<P, Extract<Method<P>, 'put'>>,
): Promise<ApiData<P, Extract<Method<P>, 'put'>>> {
  return call('put' as Extract<Method<P>, 'put'>, url, options);
}

export function del<P extends ApiPath>(
  url: P,
  options?: CallOptions<P, Extract<Method<P>, 'delete'>>,
): Promise<ApiData<P, Extract<Method<P>, 'delete'>>> {
  return call('delete' as Extract<Method<P>, 'delete'>, url, options);
}

/**
 * Monta uma URL com `{id}` substituído — os paths do contrato usam sempre UUID
 * da aplicação em `{id}` (`api-backend.md › Convenções`).
 */
export function withPath(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_all, key: string) => {
    const value = values[key];
    if (value === undefined) throw new Error(`withPath: falta o valor de {${key}}`);
    return encodeURIComponent(value);
  });
}
